// ============================================================================
// WIDGET SHELL - Uniform widget container with error boundary
// Theme-aware: dark (navy bg) or light (white bg) styling
// ============================================================================

import React, { Component } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../types/second-screen.types';
import { WIDGET_REGISTRY } from './widgets/widgetRegistry';

// Error Boundary
interface ErrorBoundaryState {
  hasError: boolean;
  error: string;
}

class WidgetErrorBoundary extends Component<
  { widgetId: string; children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: '' };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-red-400/70 text-xs gap-1 p-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>{this.props.widgetId} crashed</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// Widget Shell
interface WidgetShellProps {
  label: string;
  widgetId: string;
  config: Record<string, any>;
  Component: React.ComponentType<WidgetProps>;
}

const WidgetShell: React.FC<WidgetShellProps> = ({ label, widgetId, config, Component: WidgetComponent }) => {
  const { theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const isTransparent = WIDGET_REGISTRY[widgetId]?.transparent ?? false;

  if (isTransparent) {
    return (
      <div className="w-full h-full overflow-hidden">
        <WidgetErrorBoundary widgetId={widgetId}>
          <WidgetComponent config={config} />
        </WidgetErrorBoundary>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border flex flex-col h-full overflow-hidden ${
      isDark
        ? 'bg-navy border-white/10'
        : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <div className={`flex items-center justify-between px-3 py-1.5 border-b shrink-0 ${
        isDark ? 'border-white/5' : 'border-slate-100'
      }`}>
        <span className={`text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}>{label}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        <WidgetErrorBoundary widgetId={widgetId}>
          <WidgetComponent config={config} />
        </WidgetErrorBoundary>
      </div>
    </div>
  );
};

export default WidgetShell;
